import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Mail, Phone, MapPin } from "lucide-react";
import { usePhoneNumber } from "../hooks/usePhoneNumber";
 
// Social Icons Component
const SocialIcons = () => {
  const icons = [
    { name: "Facebook", href: "https://www.facebook.com/goldensparrowtrading/", src: "/images/icons/facebook.png" },
    { name: "Instagram", href: "https://www.instagram.com/goldensparrow.machinery/", src: "/images/icons/instagram.png" },
    { name: "TikTok", href: "https://www.tiktok.com/@goldensparrow_machinery", src: "/images/icons/tiktok.png" },
    { name: "LinkedIn", href: "#", src: "/images/icons/linkedin.png" },
  ];
 
  return (
    <div className="flex gap-4 justify-start sm:justify-start">
      {icons.map((icon) => (
        <a
          key={icon.name}
          href={icon.href}
          target={icon.href !== "#" ? "_blank" : "_self"}
          rel="noopener noreferrer"
          className="w-8 h-8 flex items-center justify-center"
          aria-label={icon.name}
        >
          <img src={icon.src} alt={icon.name} className="w-full h-full object-contain" />
        </a>
      ))}
    </div>
  );
};
 
// WhatsApp Icon
const WhatsAppIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.465 3.516" />
  </svg>
);
 
// WhatsApp Button
const WhatsAppButton = ({ whatsapp, message, className = "", productName = "" }) => {
  const handleClick = () => {
    const cleanNumber = whatsapp.replace(/\D/g, ""); // remove +, -, spaces, etc.
    const encodedMessage = encodeURIComponent(message);
    let whatsappUrl = `https://api.whatsapp.com/send?phone=${cleanNumber}&text=${encodedMessage}`;
    window.open(whatsappUrl, "_blank");
  };
 
  return (
    <button
      onClick={handleClick}
      className={`bg-[#25D366] hover:bg-[#20BA5A] text-white p-3 rounded-full shadow-2xl hover:shadow-3xl transform hover:scale-105 transition-all duration-300 group relative ${className}`}
      aria-label="Contact us on WhatsApp"
      style={{ background: "linear-gradient(135deg, #25D366 0%, #20BA5A 100%)" }}
    >
      <WhatsAppIcon />
      <div className="absolute inset-0 rounded-full bg-[#25D366] opacity-75 animate-ping"></div>
      <div className="absolute right-full -mr-20 -bottom-14 opacity-100 group-hover:opacity-100 transition-all duration-300 pointer-events-none">
        <div className="bg-white text-[#202020] px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap shadow-lg border">
          Chat with us!
        </div>
      </div>
    </button>
  );
};
 
// Main Footer Component
const Footer = () => {
  const location = useLocation();
  const {phone, whatsapp} = usePhoneNumber();
  const path = location.pathname.toLowerCase();

   console.log("Phone:", phone); // Should be string
  console.log("WhatsApp:", whatsapp); // Should be string
 
   // Define custom WhatsApp messages per page
  const pageMessages = {
    "/forklifts": "Hello! I'm looking for forklifts.",
    "/telehandlers": "Hello! I'm looking for telehandlers.",
    "/excavators": "Hello! I'm looking for excavators.",
    "/backhoe-loaders": "Hello! I'm looking for backhoe loaders.",
    "/bulldozers": "Hello! I'm looking for bulldozers.",
    "/wheel-loaders": "Hello! I'm looking for wheel loaders.",
    "/air-compressors": "Hello! I'm looking for air compressors.",
    "/skid-steer": "Hello! I'm looking for skid steers.",
    "/mini-excavator": "Hello! I'm looking for mini excavators.",
    "/lighting-towers": "Hello! I'm looking for tower lights."
  };
 
  // Get message dynamically
  const whatsappMessage = pageMessages[path] || "Hello! I'm looking for industrial equipments.";
 
  const quickLinks = [
    { label: "Home", to: "/" },
    { label: "About Us", to: "/about-us" },
    { label: "Categories", to: "/categories" },
    { label: "Brands", to: "/brands" },
    { label: "Contact", to: "/contact" },
    { label: "Privacy Policy", to: "/privacy-policy" },
    { label: "Terms and Conditions", to: "/terms-and-conditions" },
  ];
 
  const pages = [
    { label: "Forklifts", to: "/forklifts" },
    { label: "Telehandlers", to: "/telehandlers" },
    { label: "Excavators", to: "/excavators" },
    { label: "Backhoe Loaders", to: "/backhoe-loaders" },
    { label: "Bull Dozers", to: "/bulldozers" },
    { label: "Wheel Loaders", to: "/wheel-loaders" },
    { label: "Air Compressors", to: "/air-compressors" },
    { label: "Skid Steers", to: "/skid-steer" },
    { label: "Mini Excavator", to: "/mini-excavator" },
    { label: "Tower Lights", to: "/lighting-towers" },
  ];
 
  return (
    <footer className="bg-white text-black py-16 lg:py-24" style={{ boxShadow: "0px 0px 44px 0px rgba(0,0,0,0.2)" }}>
      <div className="max-w-7xl mx-auto px-6 sm:px-6 lg:px-8">
              {/* Desktop Layout (lg and above) */}
              <div className="hidden lg:grid lg:grid-cols-12 gap-8 pb-8">
                {/* Logo & Tagline - Desktop */}
                <div className="lg:col-span-3">
                  <Link to="/" className="inline-block mb-4">
                    <img
                      src="/images/logos/footer_logo_new.png"
                      alt="Golden Sparrow"
                      className="w-auto object-contain max-w-[135px]"
                      loading="eager"
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                    />
                  </Link>
                  <p className="text-black text-lg leading-relaxed">
                    Engineered for<br />
                    Performance.<br />
                    <span className="font-semibold">Trusted for Results.</span>
                  </p>
                </div>
     
                {/* Quick Links - Desktop */}
                <div className="lg:col-span-3">
                  <h4 className="text-lg font-semibold mb-4">Quick Links</h4>
                  <ul className="space-y-2 text-base">
                    <li><Link to="/" className="hover:underline transition-all">Home</Link></li>
                    <li><Link to="/about-us" className="hover:underline transition-all">About Us</Link></li>
                    <li><Link to="/categories" className="hover:underline transition-all">Categories</Link></li>
                    <li><Link to="/brands" className="hover:underline transition-all">Brands</Link></li>
                    <li><Link to="/contact" className="hover:underline transition-all">Contact</Link></li>
                    <li><Link to="/privacy-policy" className="hover:underline transition-all">Privacy Policy</Link></li>
                    <li><Link to="/terms-and-conditions" className="hover:underline transition-all">Terms and Conditions</Link></li>
                  </ul>
                </div>
     
                {/* Pages - Desktop */}
                <div className="lg:col-span-3">
                  <h4 className="text-lg font-semibold mb-4">Pages</h4>
                  <ul className="space-y-2 text-base">
                    <li><Link to="/forklifts" className="hover:underline transition-all">Forklifts</Link></li>
                    <li><Link to="/telehandlers" className="hover:underline transition-all">Telehandlers</Link></li>
                    <li><Link to="/excavators" className="hover:underline transition-all">Excavators</Link></li>
                    <li><Link to="/backhoe-loaders" className="hover:underline transition-all">Backhoe Loaders</Link></li>
                    <li><Link to="/bulldozers" className="hover:underline transition-all">Bull Dozers</Link></li>
                    <li><Link to="/wheel-loaders" className="hover:underline transition-all">Wheel Loaders</Link></li>
                    <li><Link to="/air-compressors" className="hover:underline transition-all">Air Compressors</Link></li>
                    <li><Link to="/skid-steer" className="hover:underline transition-all">Skid Steers</Link></li>
                    <li><Link to="/mini-excavator" className="hover:underline transition-all">Mini Excavator</Link></li>
                    {/* <li><Link to="/all-terrain-cranes" className="hover:underline transition-all">Cranes</Link></li> */}
                    <li><Link to="/lighting-towers" className="hover:underline transition-all">Tower Lights</Link></li>
                  </ul>
                </div>
     
                {/* Contact Info - Desktop */}
                <div className="lg:col-span-3">
                  <h4 className="text-lg font-semibold mb-4">Connect Instantly</h4>
                  <ul className="space-y-3 text-base mb-6">
                    <li className="flex items-start gap-2">
                      <Mail className="w-4 h-4 mt-1 flex-shrink-0" />
                      <a href="mailto:admin@goldensparrowuae.com" className="text-sm hover:underline transition-all break-all">
                        admin@goldensparrowuae.com
                      </a>
                    </li>
                    <li className="flex items-start gap-2">
                      <Phone className="w-4 h-4 mt-1 flex-shrink-0" />
                      <a href={`tel:${phone}`} className="hover:underline transition-all">
                        {phone}
                      </a>
                    </li>
                    <li className="flex items-start gap-2">
                      <MapPin className="w-4 h-4 mt-1 flex-shrink-0" />
                      <a
                        href="https://maps.app.goo.gl/8CQna9FwzyK4So8s5"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm hover:underline transition-all"
                      >
                        Donna Towers, Silicon Oasis, Dubai, U.A.E.
                      </a>
                    </li>
                  </ul>
                 
                  <h5 className="text-base font-semibold mb-3">Follow Us On!</h5>
                  <SocialIcons />
                </div>
              </div>
     
              {/* Mobile/Tablet Layout (below lg) */}
              <div className="lg:hidden space-y-16 pb-8">
                {/* Logo & Tagline - Mobile */}
                <div>
                  <Link to="/" className="inline-block mb-4">
                    <img
                      src="/images/logos/footer_logo_new.png"
                      alt="Golden Sparrow"
                      className="w-auto object-contain max-w-[135px]"
                      loading="eager"
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                    />
                  </Link>
                  <p className="text-black text-3xl leading-relaxed">
                    Engineered for Performance.<br />
                    <span className="font-semibold">Trusted for Results.</span>
                  </p>
                </div>
     
                {/* Two Column Layout for Mobile */}
                <div className="grid grid-cols-2 gap-6">
                  {/* Quick Links - Mobile */}
                  <div>
                    <h4 className="text-2xl font-semibold mb-3">Quick Links</h4>
                    <ul className="space-y-3 text-xl">
                      <li><Link to="/" className="hover:underline">Home</Link></li>
                      <li><Link to="/about-us" className="hover:underline">About Us</Link></li>
                      <li><Link to="/categories" className="hover:underline">Categories</Link></li>
                      <li><Link to="/brands" className="hover:underline">Brands</Link></li>
                      <li><Link to="/contact" className="hover:underline">Contact</Link></li>
                      <li><Link to="/privacy-policy" className="hover:underline">Privacy Policy</Link></li>
                      <li><Link to="/terms-and-conditions" className="hover:underline">Term & Conditions</Link></li>
                    </ul>
                  </div>
     
                  {/* Pages - Mobile */}
                  <div>
                    <h4 className="text-2xl font-semibold mb-3 text-right">Pages</h4>
                    <ul className="space-y-3 text-xl text-right">
                    <li><Link to="/forklifts" className="hover:underline transition-all">Forklifts</Link></li>
                    <li><Link to="/telehandlers" className="hover:underline transition-all">Telehandlers</Link></li>
                    <li><Link to="/excavators" className="hover:underline transition-all">Excavators</Link></li>
                    <li><Link to="/backhoe-loaders" className="hover:underline transition-all">Backhoe Loaders</Link></li>
                    <li><Link to="/bulldozers" className="hover:underline transition-all">Bull Dozers</Link></li>
                    <li><Link to="/wheel-loaders" className="hover:underline transition-all">Wheel Loaders</Link></li>
                    <li><Link to="/air-compressors" className="hover:underline transition-all">Air Compressors</Link></li>
                    <li><Link to="/skid-steer" className="hover:underline transition-all">Skid Steers</Link></li>
                    <li><Link to="/mini-excavator" className="hover:underline transition-all">Mini Excavator</Link></li>
                    {/* <li><Link to="/all-terrain-cranes" className="hover:underline transition-all">Cranes</Link></li> */}
                    <li><Link to="/lighting-towers" className="hover:underline transition-all">Tower Lights</Link></li>
                    </ul>
                  </div>
                </div>
     
                {/* Contact Info - Mobile (Full Width) */}
                <div className="space-y-8">
                  <h4 className="text-2xl font-semibold mb-3">Connect Instantly</h4>
                  <ul className="space-y-6 text-xl mb-6">
                    <li className="flex items-start gap-2">
                      <Mail className="w-5 h-5 mt-1 flex-shrink-0" />
                      <a href="mailto:admin@goldensparrowuae.com" className="hover:underline break-all">
                        admin@goldensparrowuae.com
                      </a>
                    </li>
                    <li className="flex items-start gap-2">
                      <Phone className="w-5 h-5 mt-1 flex-shrink-0" />
                      <a href={`tel:${phone}`} className="text-3xl hover:underline">
                        {phone}
                      </a>
                    </li>
                    <li className="flex items-start gap-2">
                      <MapPin className="w-5 h-5 mt-1 flex-shrink-0" />
                      <a
                        href="https://maps.app.goo.gl/8CQna9FwzyK4So8s5"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:underline"
                      >
                        Donna Towers, Silicon Oasis, Dubai, U.A.E.
                      </a>
                    </li>
                  </ul>
                 
                  <h5 className="text-xl font-semibold pt-6 text-center">Follow Us On!</h5>
                  <div className="flex justify-center">
                  <SocialIcons />
                  </div>
                </div>
              </div>
     
              {/* Bottom Bar - Same for all viewports */}
              <div className="border-t border-black pt-8 text-center font-semibold text-base sm:text-base text-black">
                <p className="">
                  &copy; {new Date().getFullYear()} Golden Sparrow Trading CO. L.L.C.{" "}
                  <span className="block sm:inline">All Rights Reserved</span>
                </p>
              </div>
            </div>
 
        {/* WhatsApp Buttons */}
        <div className="fixed bottom-28 right-14 z-50">
          <WhatsAppButton whatsapp={whatsapp} message={whatsappMessage} />
        </div>
    </footer>
  );
};
 
export default Footer;